import { useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { photoSrc } from '@/utils/api';

interface PhotoPickerProps {
  photoUrl: string | null;
  onPhotoSelected: (file: File) => void;
  onPhotoRemoved: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export default function PhotoPicker({ photoUrl, onPhotoSelected, onPhotoRemoved, size = 'md' }: PhotoPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
  };

  const displayUrl = preview || photoSrc(photoUrl);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);

    onPhotoSelected(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    onPhotoRemoved();
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`${sizeClasses[size]} rounded-2xl overflow-hidden flex items-center justify-center transition-all active:scale-95`}
        style={{
          background: displayUrl ? 'transparent' : 'var(--sf-bg-card)',
          border: displayUrl ? 'none' : '2px dashed var(--sf-border-light)',
        }}
      >
        {displayUrl ? (
          <img src={displayUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-tg-hint">
            <Camera className="w-6 h-6" />
            <span className="text-[10px]">Фото</span>
          </div>
        )}
      </button>

      {displayUrl && (
        <button
          onClick={handleRemove}
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center z-10"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
